// /dashboard — "My session" page (LLD 11 v0.2).
//
// Sections top-to-bottom:
//   1. Welcome header (alias / tenant / identity)
//   2. Active role card
//   3. Effective rules — every rule that fired for the current session, by
//      rule id + role + per-axis scope.
//   4. Role snapshots — for each role the user holds, the resolved
//      permission set rendered as catalog-grouped chips.
//   5. Session strip (live countdown)
//   6. Debug panel (lazy /api/debug/whoami)
//
// Replaces the legacy "Your access" grants table. The grants array is
// gone under v0.2; the runtime artefact is `effective_rules` +
// `role_snapshots`. Older BFF builds may not yet emit the new fields —
// the page falls back to a "Backend has not yet shipped …" notice for
// each missing block.

import { useEffect, useMemo, useState } from "react";
import { api, ApiError, useApiErrorHandler } from "@/api";
import {
  DebugPanel,
  RoleChip,
  StatusChip,
  type DebugSection,
} from "@/components";
import { useAppConfig } from "@/config";
import { useRole } from "@/role";
import { permissionClient } from "@/data/clients/permissionClient";
import { useSession } from "@/session";
import type { EffectiveRule, RoleSnapshot } from "@/session";
import type { PermissionCatalog } from "@/types/role";
import { permissionDisplayName } from "@/types/role";
import { formatRemaining, formatUtcMinute, remainingTone } from "@/utils/format";
import "./Dashboard.css";

interface WhoAmIResponse {
  identity?: Record<string, unknown>;
  session?: Record<string, unknown>;
  id_token_claims?: Record<string, unknown>;
  id_token_raw?: string;
  id_token_decoded?: {
    header?: Record<string, unknown>;
    payload?: Record<string, unknown>;
  };
}

function toEntries(
  obj: Record<string, unknown> | undefined,
): ReadonlyArray<readonly [string, unknown]> {
  if (!obj) return [];
  return Object.entries(obj);
}

export function DashboardPage(): JSX.Element {
  const { session } = useSession();
  const { currentRole, distinctRoles } = useRole();
  const { debugWhoamiEnabled } = useAppConfig();
  const handleApiError = useApiErrorHandler();

  const [now, setNow] = useState<number>(() => Date.now());
  const [whoami, setWhoami] = useState<WhoAmIResponse | null>(null);
  const [whoamiLoading, setWhoamiLoading] = useState<boolean>(false);
  const [whoamiError, setWhoamiError] = useState<string | null>(null);
  const [whoamiHidden, setWhoamiHidden] = useState<boolean>(false);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);

  // Live countdown tick — 1s cadence.
  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(handle);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const cat = await permissionClient.getCatalog();
        setCatalog(cat);
      } catch {
        // Non-fatal — chip rendering falls back to bare keys.
      }
    })();
  }, []);

  const handleWhoamiOpen = async (): Promise<void> => {
    if (
      !debugWhoamiEnabled ||
      whoamiLoading ||
      whoami !== null ||
      whoamiHidden
    ) {
      return;
    }
    setWhoamiLoading(true);
    setWhoamiError(null);
    try {
      const resp = await api.get<WhoAmIResponse>("/api/debug/whoami", {
        skipActingRole: true,
      });
      setWhoami(resp);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setWhoamiHidden(true);
      } else if (err instanceof ApiError) {
        setWhoamiError(`${err.code ?? err.status}`);
        handleApiError(err, { actionLabel: "Load auth debug" });
      } else {
        setWhoamiError("unknown");
      }
    } finally {
      setWhoamiLoading(false);
    }
  };

  const debugSections = useMemo<DebugSection[]>(() => {
    if (whoami === null) return [];
    const sections: DebugSection[] = [
      { title: "Identity", entries: toEntries(whoami.identity) },
      { title: "Session", entries: toEntries(whoami.session) },
      { title: "id_token claims", entries: toEntries(whoami.id_token_claims) },
    ];
    if (whoami.id_token_raw) {
      sections.push({
        title: "ID Token (Raw)",
        entries: [["jwt", whoami.id_token_raw]],
      });
    }
    if (whoami.id_token_decoded) {
      if (whoami.id_token_decoded.header) {
        sections.push({
          title: "ID Token Decoded — Header",
          entries: toEntries(whoami.id_token_decoded.header),
        });
      }
      if (whoami.id_token_decoded.payload) {
        sections.push({
          title: "ID Token Decoded — Payload",
          entries: toEntries(whoami.id_token_decoded.payload),
        });
      }
    }
    return sections;
  }, [whoami]);

  const groupForKey = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (!catalog) return map;
    for (const g of catalog.groups) {
      for (const p of g.permissions) {
        map.set(p.key, g.label);
      }
    }
    return map;
  }, [catalog]);

  const labelForKey = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (!catalog) return map;
    for (const g of catalog.groups) {
      for (const p of g.permissions) {
        map.set(p.key, permissionDisplayName(p));
      }
    }
    return map;
  }, [catalog]);

  if (session === null) {
    return (
      <section className="dashboard">
        <header className="page-header">
          <h1 className="page-title">Dashboard</h1>
        </header>
        <div className="dashboard__loading">Loading session…</div>
      </section>
    );
  }

  const tone = remainingTone(session.access_expires_at, now);
  const effectiveRules: EffectiveRule[] = session.effective_rules ?? [];
  const roleSnapshots: Record<string, RoleSnapshot> =
    session.role_snapshots ?? {};
  const hasNewShape =
    session.effective_rules !== undefined ||
    session.role_snapshots !== undefined;

  return (
    <section className="dashboard">
      <header className="dashboard__welcome">
        <h1 className="dashboard__welcome-title">Welcome, {session.alias}</h1>
        <div className="dashboard__welcome-meta">
          <code className="mono mono--sm">{session.tenant_id}</code>
          <span aria-hidden="true"> · </span>
          <code className="mono mono--sm">{session.ring_user_id}</code>
        </div>
      </header>

      <section className="page-section">
        <h2 className="page-section__title">Active role</h2>
        <div className="card dashboard__role-card">
          {currentRole ? (
            <RoleChip role={currentRole} size="md" />
          ) : (
            <span className="dashboard__muted">No role selected</span>
          )}
          <span className="dashboard__role-caption">
            {distinctRoles.length > 1
              ? "Change role via the top-right switcher"
              : "You hold one role; no switch available."}
          </span>
        </div>
      </section>

      <section className="page-section">
        <h2 className="page-section__title">Effective rules</h2>
        {!hasNewShape && (
          <div className="dashboard__notice">
            <StatusChip tone="warning">Backend endpoint not yet deployed</StatusChip>
            <p className="dashboard__muted" style={{ marginTop: 6 }}>
              <code className="mono">/api/session/me</code> has not yet
              shipped the LLD 11 v0.2 fields. This block lights up after
              the BFF rolls.
            </p>
          </div>
        )}
        {hasNewShape && effectiveRules.length === 0 && (
          <div className="dashboard__muted">
            No rules fired for this session — your access is empty until a
            group→role rule routes you.
          </div>
        )}
        {effectiveRules.length > 0 && (
          <ul className="dashboard__rules">
            {effectiveRules.map((r) => (
              <li key={r.rule_id} className="dashboard__rule">
                <div className="dashboard__rule-header">
                  <strong>{r.rule_name ?? r.rule_id}</strong>
                  <code className="mono mono--sm dashboard__muted">
                    {r.rule_id}
                  </code>
                  <RoleChip role={r.role_id} size="sm" />
                </div>
                <div className="dashboard__rule-scopes">
                  <ScopeChip
                    label="location"
                    mode={r.location_scope.mode}
                    ids={r.location_scope.ids}
                  />
                  <ScopeChip
                    label="device"
                    mode={r.device_scope.mode}
                    ids={r.device_scope.ids}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="page-section">
        <h2 className="page-section__title">Role snapshots</h2>
        {!hasNewShape && (
          <div className="dashboard__notice">
            <StatusChip tone="warning">Backend endpoint not yet deployed</StatusChip>
          </div>
        )}
        {hasNewShape && Object.keys(roleSnapshots).length === 0 && (
          <div className="dashboard__muted">No roles in this session.</div>
        )}
        {Object.keys(roleSnapshots).length > 0 && (
          <div className="dashboard__roles">
            {Object.values(roleSnapshots).map((snap) => (
              <article key={snap.role_id} className="dashboard__role-snapshot">
                <header className="dashboard__role-snapshot-header">
                  <RoleChip role={snap.role_id} size="md" />
                  <span style={{ fontWeight: 600 }}>
                    {snap.display_name ?? snap.role_id}
                  </span>
                  <span className="dashboard__muted">
                    {snap.permissions.length} permissions
                  </span>
                </header>
                <PermissionsByGroup
                  permissions={snap.permissions}
                  groupForKey={groupForKey}
                  labelForKey={labelForKey}
                />
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="page-section">
        <h2 className="page-section__title">Session</h2>
        <div className={`dashboard__session-strip dashboard__session-strip--${tone}`}>
          <span>
            Started <strong>{formatUtcMinute(session.started_at ?? null)}</strong>
          </span>
          <span aria-hidden="true"> · </span>
          <span>
            Access expires{" "}
            <strong>{formatUtcMinute(session.access_expires_at)}</strong> (
            {formatRemaining(session.access_expires_at, now)})
          </span>
          <span aria-hidden="true"> · </span>
          <span>
            Hard max <strong>{formatUtcMinute(session.session_max_at)}</strong>
          </span>
        </div>
      </section>

      {debugWhoamiEnabled && !whoamiHidden && (
        <section className="page-section">
          <DebugPanel
            sections={debugSections}
            loading={whoamiLoading}
            errorMessage={whoamiError}
            onOpen={handleWhoamiOpen}
          />
        </section>
      )}
    </section>
  );
}

function ScopeChip({
  label,
  mode,
  ids,
}: {
  label: string;
  mode: "ALL" | "ALLOW" | "DENY";
  ids: string[];
}): JSX.Element {
  const tone =
    mode === "ALL" ? "info" : mode === "ALLOW" ? "success" : "danger";
  const text =
    mode === "ALL"
      ? `${label}: all`
      : `${label}: ${mode.toLowerCase()} ${
          ids.length === 0
            ? "(none)"
            : `[${ids.slice(0, 3).join(", ")}${ids.length > 3 ? `, +${ids.length - 3}` : ""}]`
        }`;
  return <StatusChip tone={tone}>{text}</StatusChip>;
}

function PermissionsByGroup({
  permissions,
  groupForKey,
  labelForKey,
}: {
  permissions: string[];
  groupForKey: Map<string, string>;
  labelForKey: Map<string, string>;
}): JSX.Element {
  const grouped = useMemo(() => {
    const buckets = new Map<string, string[]>();
    for (const k of permissions) {
      const g = groupForKey.get(k) ?? "Other";
      const arr = buckets.get(g) ?? [];
      arr.push(k);
      buckets.set(g, arr);
    }
    return Array.from(buckets.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
  }, [permissions, groupForKey]);

  if (permissions.length === 0) {
    return <div className="dashboard__muted">(empty)</div>;
  }
  return (
    <div className="dashboard__perm-groups">
      {grouped.map(([group, keys]) => (
        <div key={group} className="dashboard__perm-group">
          <div className="dashboard__perm-group-label">{group}</div>
          <ul className="dashboard__perm-chips">
            {keys.map((k) => (
              <li key={k} className="dashboard__perm-chip">
                <code className="mono mono--sm">{k}</code>
                {labelForKey.get(k) && (
                  <span className="dashboard__muted">
                    {labelForKey.get(k)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
